import React, { useEffect } from "react";

import { Controller, useForm } from "react-hook-form";

import CommonFormElement from "../UI/CommonFormElements/CommonFormElement";
import ContinueButton from "../ContinueButton/ContinueButton";

import { IonInput } from "@ionic/react";

import classNames from "classnames/bind";
import styles from "./AddressForm.module.scss";
import commonFormStyles from "../UI/CommonFormElements/CommonFormElements.module.scss";
import componentStyles from "./AddressForm.module.scss";

import { useCustomerAddressesStateData } from "../../providers/CustomerAddresses/CustomerAddressesStateContext";
import Checkbox from "../Checkbox/Checkbox";
import { AddressInput } from "@core/types";
import { CityCombobox } from "./CityCombobox";
import ApartmentDetails from "./ApartmentDetails";
import { useTranslation } from "react-i18next";
import { City } from "./types";
import { useConfig } from "../../providers/config";

const cx = classNames.bind(styles);
const cn = classNames.bind(componentStyles);

const commonForm = classNames.bind(commonFormStyles);

interface AddressFormProps {
  onSubmit: (data: any) => void;
  cities: City[];
}

const AddressForm = ({ onSubmit, cities }: AddressFormProps) => {
  const { t } = useTranslation();
  const { customerAddress } = useCustomerAddressesStateData();
  const config = useConfig();
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid },
    setValue,
  } = useForm<AddressInput>({
    defaultValues: { ...customerAddress, city: "", isFlat: config.addressSettings.showFlatDelivery },
    mode: "onChange",
    shouldUnregister: true,
  });

  useEffect(() => {
    if (cities && customerAddress) {
      const city = cities.find((_city) => _city.name.includes(customerAddress?.city || ""));
      city?.name && setValue("city", city.name, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    }
  }, [cities, customerAddress]);

  const filteredCities =
    watch("city") !== undefined && watch("city") !== ""
      ? cities.filter((city) => {
          return city.name.toLowerCase().replace(/\s+/g, "").includes(watch("city")!.toLowerCase().replace(/\s+/g, ""));
        })
      : cities;

  const cityFieldDisabled = cities.length < 1;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={cn("form")}>
      <Controller
        key="city"
        name="city"
        control={control}
        rules={{
          required: true,
          validate: (cityName) => cities.some((city) => city.name === cityName),
        }}
        render={({ field: { onChange, value }, fieldState: { invalid } }) => {
          return (
            <CityCombobox
              value={value!}
              invalid={invalid}
              onChange={onChange}
              disabled={cityFieldDisabled}
              filteredCities={filteredCities}
            />
          );
        }}
      />

      <Controller
        control={control}
        rules={{
          required: t("Required field"),
        }}
        name="street"
        render={({ field: { onChange, value } }) => (
          <>
            <CommonFormElement isRequired label={t("Street")} isValid={!!value} errorMessage={errors.street?.message}>
              <IonInput
                type="text"
                value={value}
                onIonChange={(e) => {
                  onChange(e.detail.value!);
                }}
                placeholder={t("Street input placeholder")}
                className={commonForm("commonElement", { commonInvalid: !value?.length })}
              />
            </CommonFormElement>
          </>
        )}
      />

      <Controller
        control={control}
        name="house"
        rules={{
          required: t("Required field"),
        }}
        render={({ field: { onChange, value } }) => (
          <>
            <CommonFormElement
              isRequired
              label={t("House number")}
              errorMessage={errors.house?.message}
              isValid={!!value}
            >
              <IonInput
                type="text"
                value={value}
                onIonChange={(e) => onChange(e.detail.value!)}
                placeholder={t("House number input placeholder")}
                className={commonForm("commonElement", { commonInvalid: !watch("house")?.length })}
              />
            </CommonFormElement>
          </>
        )}
      />

      {config.addressSettings.showFlatDelivery && (
        <Controller
          control={control}
          name="isFlat"
          render={({ field: { onChange, value } }) => (
            <Checkbox
              label={t("Delivery to the apartment")}
              itemClassName={cx("checkboxItem")}
              checked={!!value}
              onIonChange={(e: any) => onChange(e.detail.checked)}
            />
          )}
        />
      )}

      <ApartmentDetails control={control} errors={errors} watch={watch} />

      <ContinueButton
        className={cx("modal__button")}
        onClick={() => false}
        type="submit"
        disabled={!isValid}
        buttonText={t("Save Address")}
      />
    </form>
  );
};

export { AddressForm };
